import { Component, OnDestroy, OnInit } from '@angular/core';
import { dayjs } from '@karve.it/core';
import { EMPTY, Subject, Subscription, forkJoin } from 'rxjs';
import { catchError, debounceTime, switchMap, take, tap } from 'rxjs/operators';

import {
  DataOnlyDocument,
  UpdateJobMetadataGQL,
} from '../../../generated/graphql.generated';
import { PlusApollo } from '../../core/auth/graphql.module';
import { GraphQLModule } from '../../core/public-api';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { parseEmbeddedUrl, parseHyperlink } from '../../utilities/generic.util';
import { DashboardStore } from '../dashboardv2.store';

const CALC_OUTPUT_PREFIX = 'calculatorOutput__';

const EMPTY_OR_MISSING = new Set([
  'none', '', 'inventory ok', 'ops approved', 'truck ok', 'crew ok', 'labor ok', 'welcome ok', 'ops notes', 'notes',
  'calculator_inventoryok', 'calculator_opsapproved', 'calculator_opsnotes',
]);

interface JobCalcData {
  crewSize: number;
  hoursRange: string;
  weight: string;
  weightDisplay: string;
  laborCostMin: number;
  hourlyRate: number;
  inventoryOk: boolean;
  opsApproved: boolean;
  truckOk: boolean;
  crewOk: boolean;
  laborOk: boolean;
  welcomeOk: boolean;
  opsNotes: string;
}

interface OpsEvent {
  jobCode: string;
  jobId: string;
  customer: string;
  route: string;
  eventStatus: string;
  eventType: string;
  eventBookedStatusDate: string;
  jobPaidTotal: string;
  discountedSubTotal: string;
  contractSigned: boolean | null;
  salesAgent: string;
  inventoryWeight: string;
  weightFromCalculator: boolean;
  eventDurationInMinutes: number;
  calculatedCrewSize: number;
  calculatedCrewSizeDisplay: string;
  calculatedLabor: number;
  accurateDrive: number;
  calculatedTotal: number;
  billedCrewSize: number;
  billedCrewSizeDisplay: string;
  billedLabor: number;
  billedDrive: number;
  billedTotal: number;
  bookedTotal: number;
  calculatedDiff: number;
  billedDiff: number;
  eventStart: string;
  eventId: string;
  inventoryOk: boolean;
  opsApproved: boolean;
  truckOk: boolean;
  crewOk: boolean;
  laborOk: boolean;
  welcomeOk: boolean;
  opsNotes: string;
}

interface DayGroup {
  date: string;
  displayDate: string;
  events: OpsEvent[];
  eventCount: number;
  totalHours: number;
  totalRevenue: number;
  daysUntilMove: number | null;
}

const OPS_READINESS_DATA_COLUMNS = [
  'customer',
  'eventRoute',
  'eventStatus',
  'eventType',
  'eventBookedStatusDate',
  'jobPaidTotal',
  'discountedSubTotal',
  'salesAgent',
  'crewSize',
  'eventDurationInMinutes',
  'totalTravelTime',
  'dockToDockDriveTime',
  'billedCrewSize',
  'billedLaborHours',
  'billedTravelHours',
  'billedTotal',
  'inventoryTotalWeight',
  'estimateSigned',
  'eventBookingDate',
  'event',
  'job',
];

const JOB_CALCULATOR_COLUMNS = [
  'job',
  'calculator_crewSize',
  'calculator_hoursRange',
  'calculator_weight',
  'calculator_weightDisplay',
  'calculator_laborCostMin',
  'calculator_hourlyRate',
  'calculator_inventoryOk',
  'calculator_opsApproved',
  'calculator_truckOk',
  'calculator_crewOk',
  'calculator_laborOk',
  'calculator_welcomeOk',
  'calculator_opsNotes',
];

@Component({
  selector: 'app-dashboard-ops-readiness',
  standalone: false,
  templateUrl: './dashboard-ops-readiness.component.html',
  styleUrls: [
    '../dashboardv2.component.scss',
    './dashboard-ops-readiness.component.scss',
  ],
})
export class DashboardOpsReadinessComponent implements OnInit, OnDestroy {

  dayGroups: DayGroup[] = [];
  loading = true;
  lastReload = 0;
  totalEvents = 0;
  totalHours = 0;
  expandedDays = new Set<string>();
  showDetails = false;
  showSharedEvents = false;
  sharedFilterOptions = [
    { label: 'This Zone Only', value: false },
    { label: 'Include Shared In', value: true },
  ];

  private reload$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private dashboardStore: DashboardStore,
    private graphqlModule: GraphQLModule,
    private apollo: PlusApollo,
    private updateJobGQL: UpdateJobMetadataGQL,
    private notify: FreyaNotificationsService,
    private detailsHelper: DetailsHelperService,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.dashboardStore.unixPeriod$.subscribe(() => {
        this.reload$.next();
      }),
    );

    this.subs.push(
      this.reload$.pipe(
        debounceTime(100),
        switchMap(() => {
          const zone = this.graphqlModule.zone.value;
          if (!zone) {
            this.loading = false;
            return EMPTY;
          }

          this.loading = true;
          this.totalEvents = 0;
          this.totalHours = 0;
          this.notesDebounce.forEach(({ sub }) => sub.unsubscribe());
          this.notesDebounce.clear();

          return this.dashboardStore.unixPeriod$.pipe(
            take(1),
            switchMap(({ start, end }) => {
              return forkJoin({
                events: this.apollo.query({
                  query: DataOnlyDocument,
                  // Requirement: #6547 - no-cache rather than Apollo's default cache-first.
                  // This is an opaque report blob (no `id`, so it cannot be normalized) that
                  // nothing else reads from the cache. Caching it stranded a stale entry that
                  // survived until logout, so edits made on this dashboard reappeared reverted
                  // after navigating away and back.
                  fetchPolicy: 'no-cache',
                  variables: {
                    start,
                    end,
                    periodType: 'eventStart',
                    groupBy: 'event',
                    dataColumns: OPS_READINESS_DATA_COLUMNS,
                    filter: {
                      job: {
                        getJobsOfState: ['open', 'closed', 'archived'],
                      },
                      event: {
                        getEventsOfStatus: ['booked', 'confirmed', 'completed'],
                        ...(!this.showSharedEvents && { getEventsOfSharedStatus: ['not-shared'] }),
                      },
                    },
                    sort: 'eventBookingDate ASC',
                    performance: 'skip-count',
                    skip: 0,
                    limit: -1,
                    zone,
                  } as any,
                }),
                jobs: this.apollo.query({
                  query: DataOnlyDocument,
                  // Requirement: #6547 - carries the calculatorOutput__* checkbox and notes
                  // values edited on this dashboard, so a cached read here is what made the
                  // user's own changes look like they had reverted. Keep this in step with the
                  // `events` query above: groupByDay joins the two by job id, so if only one
                  // bypasses the cache they can resolve from different zones or points in time
                  // and the join silently drops every row.
                  fetchPolicy: 'no-cache',
                  variables: {
                    start,
                    end,
                    periodType: 'eventStart',
                    groupBy: 'job',
                    dataColumns: JOB_CALCULATOR_COLUMNS,
                    filter: {
                      job: {
                        getJobsOfState: ['open', 'closed', 'archived'],
                      },
                    },
                    sort: null,
                    performance: 'skip-count',
                    skip: 0,
                    limit: -1,
                    zone,
                  } as any,
                }),
              });
            }),
            tap(({ events, jobs }: any) => {
              const eventRows: string[][] = events?.data?.previewReport?.data?.rows ?? [];
              const eventHeaders: string[] = events?.data?.previewReport?.data?.headers ?? [];
              const jobRows: string[][] = jobs?.data?.previewReport?.data?.rows ?? [];
              const jobHeaders: string[] = jobs?.data?.previewReport?.data?.headers ?? [];

              const jobCalcData = this.buildJobCalcMap(jobHeaders, jobRows);
              this.dayGroups = this.groupByDay(eventHeaders, eventRows, jobCalcData);
              this.totalEvents = this.dayGroups.reduce((sum, g) => sum + g.eventCount, 0);
              this.totalHours = this.dayGroups.reduce((sum, g) => sum + g.totalHours, 0);
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
            }),
            catchError(() => {
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
              return EMPTY;
            }),
          );
        }),
      ).subscribe(),
    );

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.notesDebounce.forEach(({ sub }) => sub.unsubscribe());
    this.notesDebounce.clear();
    this.reload$.complete();
  }

  openJobDetails(event: Event, evt: OpsEvent): void {
    event.preventDefault();
    if (evt.jobId) {
      this.detailsHelper.open('job', { id: evt.jobId });
    }
  }

  openJobNewTab(evt: OpsEvent): void {
    if (evt.jobId) {
      window.open('/jobs/' + evt.jobId + '/overview', '_blank');
    }
  }

  private parseCrewSizeFromChargeName(name: string): number {
    if (!name || name === 'None') return 0;
    const match = name.match(/(\d+)\s*(?:Man|Crew)/i);
    return match ? parseInt(match[1], 10) : 0;
  }

  refresh(): void {
    this.reload$.next();
  }

  toggleDayExpanded(date: string): void {
    if (this.expandedDays.has(date)) {
      this.expandedDays.delete(date);
    } else {
      this.expandedDays.add(date);
    }
  }

  isDayExpanded(date: string): boolean {
    return this.expandedDays.has(date);
  }

  formatHours(hours: number): string {
    if (!hours && hours !== 0) return '\u2014';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  }

  formatDiff(diff: number): string {
    if (!diff && diff !== 0) return '';
    const prefix = diff > 0 ? '+' : '';
    return `${prefix}${this.formatHours(diff)}`;
  }

  diffClass(diff: number): string {
    if (diff > 0.25) return 'ops-scope-over';
    if (diff < 0) return 'ops-scope-under';
    return 'ops-scope-neutral';
  }



  toggleInventoryOk(evt: OpsEvent, checked: boolean): void {
    evt.inventoryOk = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'inventoryOk']: String(checked) });
  }

  toggleOpsApproved(evt: OpsEvent, checked: boolean): void {
    evt.opsApproved = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'opsApproved']: String(checked) });
  }

  toggleTruckOk(evt: OpsEvent, checked: boolean): void {
    evt.truckOk = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'truckOk']: String(checked) });
  }

  toggleCrewOk(evt: OpsEvent, checked: boolean): void {
    evt.crewOk = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'crewOk']: String(checked) });
  }

  toggleLaborOk(evt: OpsEvent, checked: boolean): void {
    evt.laborOk = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'laborOk']: String(checked) });
  }

  toggleWelcomeOk(evt: OpsEvent, checked: boolean): void {
    evt.welcomeOk = checked;
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'welcomeOk']: String(checked) });
  }

  onOpsNotesInput(evt: OpsEvent): void {
    if (!this.notesDebounce.has(evt.jobId)) {
      const subject = new Subject<string>();
      const sub = subject.pipe(debounceTime(800)).subscribe((val) => {
        this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'opsNotes']: val });
      });
      this.notesDebounce.set(evt.jobId, { subject, sub });
    }
    this.notesDebounce.get(evt.jobId)!.subject.next(evt.opsNotes);
  }

  private savingJobs = new Set<string>();
  private pendingMetadata = new Map<string, Record<string, string>>();
  private notesDebounce = new Map<string, { subject: Subject<string>; sub: Subscription }>();
  private saveJobMetadata(jobId: string, metadata: Record<string, string>): void {
    if (this.savingJobs.has(jobId)) {
      const existing = this.pendingMetadata.get(jobId) ?? {};
      Object.assign(existing, metadata);
      this.pendingMetadata.set(jobId, existing);
      return;
    }
    this.savingJobs.add(jobId);
    this.updateJobGQL.mutate({
      updateJobs: { jobId, metadata },
    }).subscribe({
      next: (result) => {
        if (!result.data?.updateJobs?.isSuccess) {
          this.savingJobs.delete(jobId);
          this.pendingMetadata.delete(jobId);
          this.notify.error('Error', 'Failed to save ops data');
          return;
        }
        this.savingJobs.delete(jobId);
        const pending = this.pendingMetadata.get(jobId);
        if (pending) {
          this.pendingMetadata.delete(jobId);
          this.saveJobMetadata(jobId, pending);
        }
      },
      error: () => {
        this.savingJobs.delete(jobId);
        this.pendingMetadata.delete(jobId);
        this.notify.error('Error', 'Failed to save ops data');
      },
    });
  }

  // Requirement: #6622 - the report returns '' for an event with no Truck/Delivery asset
  // (and 'None' if the column is missing); render both as the dispatch board's 'Unassigned'.
  private parseRoute(raw: string): string {
    return !raw || raw === 'None' ? '' : raw;
  }

  private parseName(raw: string): string {
    const embedded = parseEmbeddedUrl(raw);
    if (embedded) {
      return embedded.before;
    }
    const hyperlink = parseHyperlink(raw);
    if (hyperlink) {
      return hyperlink.text;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return [parsed.givenName, parsed.familyName].filter(Boolean).join(' ') || raw;
      }
    } catch {}
    return raw;
  }

  private buildJobCalcMap(headers: string[], rows: string[][]): Map<string, JobCalcData> {
    const getVal = (row: string[], col: string) => row[headers.indexOf(col)] ?? '';
    const map = new Map<string, JobCalcData>();

    for (const row of rows) {
      const jobRaw = getVal(row, 'Job');
      const jobEmbedded = parseEmbeddedUrl(jobRaw);
      let jobId = '';
      if (jobEmbedded) {
        const segments = jobEmbedded.url.split('/').filter(Boolean);
        jobId = segments[segments.length - 1] || '';
      }
      if (!jobId) continue;

      const crewSize = parseFloat(getVal(row, 'Crew Size')) || 0;
      const hoursRange = getVal(row, 'Hours Range');
      const weight = getVal(row, 'Weight');
      const weightDisplay = getVal(row, 'Weight Display');
      const laborCostMin = parseFloat(getVal(row, 'Labor Cost Min')) || 0;
      const hourlyRate = parseFloat(getVal(row, 'Hourly Rate')) || 0;
      const inventoryOkRaw = getVal(row, 'Inventory Ok');
      const inventoryOk = inventoryOkRaw === 'true' || inventoryOkRaw === '1';
      const opsApprovedRaw = getVal(row, 'Ops Approved');
      const opsApproved = opsApprovedRaw === 'true' || opsApprovedRaw === '1';
      const truckOkRaw = getVal(row, 'Truck Ok');
      const truckOk = truckOkRaw === 'true' || truckOkRaw === '1';
      const crewOkRaw = getVal(row, 'Crew Ok');
      const crewOk = crewOkRaw === 'true' || crewOkRaw === '1';
      const laborOkRaw = getVal(row, 'Labor Ok');
      const laborOk = laborOkRaw === 'true' || laborOkRaw === '1';
      const welcomeOkRaw = getVal(row, 'Welcome Ok');
      const welcomeOk = welcomeOkRaw === 'true' || welcomeOkRaw === '1';
      let opsNotes = getVal(row, 'Ops Notes');
      if (!opsNotes || EMPTY_OR_MISSING.has(opsNotes.toLowerCase())) {
        opsNotes = '';
      }

      map.set(jobId, {
        crewSize, hoursRange, weight, weightDisplay, laborCostMin, hourlyRate,
        inventoryOk, opsApproved, truckOk, crewOk, laborOk, welcomeOk, opsNotes,
      });
    }

    return map;
  }

  private groupByDay(headers: string[], rows: string[][], jobCalcData: Map<string, JobCalcData>): DayGroup[] {
    const getVal = (row: string[], col: string) => row[headers.indexOf(col)] ?? '';

    const map = new Map<string, OpsEvent[]>();

    for (const row of rows) {
      const eventBookingDate = getVal(row, 'Event Booking Date');
      if (!eventBookingDate || eventBookingDate === 'Not booked') continue;

      const eventType = getVal(row, 'Event Type');
      if (!eventType || eventType.toLowerCase() === 'virtualestimate' || eventType.toLowerCase() === 'estimating') continue;

      const date = dayjs(eventBookingDate).format('YYYY-MM-DD');

      const eventDurationInMinutes = parseFloat(getVal(row, 'Event Duration (minutes)')) || 0;
      const totalTravelTime = parseFloat(getVal(row, 'Total Travel Time')) || 0;
      const dockToDockDriveTime = parseFloat(getVal(row, 'Dock-to-Dock Drive Time')) || 0;
      const discountedSubTotalRaw = getVal(row, 'Net subtotal');

      const billedCrewSizeRaw = getVal(row, 'Billed Crew Size');
      const billedLaborHours = parseFloat(getVal(row, 'Billed Labor Hours')) || 0;
      const billedTravelHours = parseFloat(getVal(row, 'Billed Travel Hours')) || 0;
      const inventoryTotalWeightRaw = getVal(row, 'Inventory Total Weight');

      const jobRaw = getVal(row, 'Job');
      const jobEmbedded = parseEmbeddedUrl(jobRaw);
      let jobCode = jobRaw;
      let jobId = '';
      if (jobEmbedded) {
        jobCode = jobEmbedded.before;
        const segments = jobEmbedded.url.split('/').filter(Boolean);
        jobId = segments[segments.length - 1] || '';
      }

      const calcData = jobCalcData.get(jobId);
      const calcCrewSize = calcData?.crewSize || 0;
      const calcLaborHours = (calcData?.laborCostMin && calcData?.hourlyRate) ? calcData.laborCostMin / calcData.hourlyRate : 0;

      let inventoryWeight = '';
      let weightFromCalculator = false;

      const inventoryTotalWeight = parseFloat(inventoryTotalWeightRaw) || 0;
      if (inventoryTotalWeight > 0) {
        inventoryWeight = `${inventoryTotalWeight} lbs`;
      } else {
        const calcWeightDisplay = calcData?.weightDisplay || '';
        const hasInventory = calcWeightDisplay.includes('(Inventory)');
        const hasManual = calcWeightDisplay.includes('(Manual)');
        if (hasInventory || hasManual) {
          const calcWeight = calcData?.weight || '';
          inventoryWeight = calcWeight ? `${calcWeight} lbs` : '';
        } else if (calcData?.weight) {
          inventoryWeight = `${calcData.weight} lbs`;
          weightFromCalculator = true;
        }
      }

      if (inventoryWeight === '0 lbs') {
        inventoryWeight = '';
        weightFromCalculator = false;
      }

      const calculatedCrewSize = calcCrewSize;
      const calculatedCrewSizeDisplay = calculatedCrewSize === 0 ? '\u2014' : String(calculatedCrewSize);

      const billedCrewSize = this.parseCrewSizeFromChargeName(billedCrewSizeRaw);
      const billedCrewSizeDisplay = billedCrewSize === 0 ? '\u2014' : String(billedCrewSize);

      const dockToDockDriveHours = (dockToDockDriveTime || totalTravelTime) / 3600;
      const calculatedLabor = calcLaborHours > 0 ? calcLaborHours : (eventDurationInMinutes / 60);
      const accurateDrive = dockToDockDriveHours;
      const calculatedTotal = calculatedLabor + accurateDrive;

      const bookedTotal = eventDurationInMinutes / 60;
      const billedDrive = billedTravelHours;
      const billedTotal = billedLaborHours + billedTravelHours;

      const calculatedDiff = calculatedTotal > 0 ? bookedTotal - calculatedTotal : 0;
      const billedDiff = billedTotal > 0 ? bookedTotal - billedTotal : 0;

      const eventBookedStatusDate = getVal(row, 'Event Booked At');
      const estimateSignedRaw = getVal(row, 'Estimate Signed');
      const contractSigned = estimateSignedRaw === 'Yes';

      const event: OpsEvent = {
        jobCode,
        jobId,
        customer: this.parseName(getVal(row, 'Customer')),
        route: this.parseRoute(getVal(row, 'Route')),
        eventStatus: getVal(row, 'Event Status'),
        eventType,
        eventBookedStatusDate,
        jobPaidTotal: getVal(row, 'Job Paid Total'),
        discountedSubTotal: discountedSubTotalRaw,
        contractSigned,
        salesAgent: this.parseName(getVal(row, 'Sales Agent')),
        inventoryWeight,
        weightFromCalculator,
        eventDurationInMinutes,
        calculatedCrewSize,
        calculatedCrewSizeDisplay,
        calculatedLabor,
        accurateDrive,
        calculatedTotal,
        billedCrewSize,
        billedCrewSizeDisplay,
        billedLabor: billedLaborHours,
        billedDrive,
        billedTotal,
        bookedTotal,
        calculatedDiff,
        billedDiff,
        eventStart: eventBookingDate,
        eventId: getVal(row, 'Event'),
        inventoryOk: calcData?.inventoryOk ?? false,
        opsApproved: calcData?.opsApproved ?? false,
        truckOk: calcData?.truckOk ?? false,
        crewOk: calcData?.crewOk ?? false,
        laborOk: calcData?.laborOk ?? false,
        welcomeOk: calcData?.welcomeOk ?? false,
        opsNotes: calcData?.opsNotes ?? '',
      };

      if (!map.has(date)) {
        map.set(date, []);
      }
      map.get(date)!.push(event);
    }

    const groups: DayGroup[] = [];
    const sortedDates = Array.from(map.keys()).sort();

    for (const d of sortedDates) {
      const events = map.get(d)!.slice().sort((a, b) => {
        if (!a.eventBookedStatusDate || a.eventBookedStatusDate === 'Not booked') return 1;
        if (!b.eventBookedStatusDate || b.eventBookedStatusDate === 'Not booked') return -1;
        return a.eventBookedStatusDate.localeCompare(b.eventBookedStatusDate);
      });
      const totalHours = events.reduce((sum, e) => sum + e.eventDurationInMinutes, 0) / 60;
      const totalRevenue = events.reduce((sum, e) => {
        const val = parseFloat(String(e.discountedSubTotal).replace(/[$,]/g, '')) || 0;
        return sum + val;
      }, 0);
      const dateObj = dayjs(d);
      const today = dayjs().startOf('day');
      const daysUntilMove = dateObj.isAfter(today) ? dateObj.diff(today, 'day') : null;

      groups.push({
        date: d,
        displayDate: dayjs(d).format('dddd, MMMM D, YYYY'),
        events,
        eventCount: events.length,
        totalHours: Math.round(totalHours * 10) / 10,
        totalRevenue,
        daysUntilMove,
      });
    }

    return groups;
  }
}
